<script setup lang="ts">
import type { LocationSectionProps } from './index';

import { cn } from '@/utils';
import GmapMap from '~/core/service/googleMaps/components/GmapMap/GmapMap.vue';
import GmapMarker from '~/core/service/googleMaps/components/GmapMarker/GmapMarker.vue';
import GmapQuadrilateral from '~/core/service/googleMaps/components/GmapQuadrilateral/GmapQuadrilateral.vue';
import { getQuadrilateral } from '~/core/service/googleMaps/pluscode';
import { cva } from 'class-variance-authority';

import { MapCondominiumConfig } from '~/components/condominium';
import TextLink from '~/components/ui/text-link/TextLink.vue';

import IconMaterialSymbolsLightMapOutline from '~icons/material-symbols-light/map-outline';

const props = withDefaults(defineProps<LocationSectionProps>(), {
	variant: 'white',
	title: 'Localização',
	cta: 'Mapa ampliado',
	showQuadrilateral: true,
	showMarker: false,
	zoom: 12,
	minZoom: 14,
	maxZoom: 17
});

const emit = defineEmits<{
	(e: 'cta-clicked'): void;
}>();

const quadrilateral = computed(() => getQuadrilateral(props.pluscodeGroup, 1));

const sectionVariant = cva('', {
	variants: {
		type: {
			white: 'flex flex-col gap-4 md:gap-6',
			primary:
				'flex flex-col gap-2 bg-primary px-6 py-5 leading-[18px] text-white md:flex-row'
		}
	},
	defaultVariants: {
		type: 'white'
	}
});

const handleCtaClicked = () => {
	emit('cta-clicked');
};
</script>

<template>
	<div class="flex w-full flex-col">
		<Skeleton
			:is-active="isLoading"
			variant="white"
			:class="
				cn(
					props.variant === 'primary' ? 'mb-8' : '',
					'h-auto min-h-[22px] w-[200px]'
				)
			"
		>
			<h2
				class="font-matter-sq text-[32px] font-normal leading-[42px] tracking-[-0.02em] text-primary"
			>
				{{ props.title }}
			</h2>
		</Skeleton>
		<template v-if="props.variant === 'white'">
			<div :class="sectionVariant({ type: 'white' })">
				<div class="flex w-full flex-col justify-between gap-4 md:flex-row">
					<Skeleton
						:is-active="isLoading"
						variant="white"
						class="min-h-5 min-w-[188px]"
					>
						<span class="text-base text-primary">
							{{ `${region}, ${city} - ${state}` }}
						</span>
					</Skeleton>

					<Skeleton
						:is-active="isLoading"
						variant="white"
						class="h-auto min-h-5 min-w-[160px]"
					>
						<TextLink
							class="flex items-center gap-1"
							variant="dark"
							@click="handleCtaClicked"
						>
							<IconMaterialSymbolsLightMapOutline class="size-5 text-primary" />
							{{ props.cta }}
						</TextLink>
					</Skeleton>
				</div>
			</div>
		</template>

		<ClientOnly fallback-tag="div">
			<template #fallback>
				<div
					class="relative h-[340px] w-full bg-map-placeholder-image bg-cover bg-no-repeat"
				/>
			</template>

			<Skeleton
				v-if="isLoading"
				:is-active="isLoading"
				variant="white"
				class="h-[340px] w-full"
			/>

			<GmapMap
				v-else
				:center="centerCoordinates || quadrilateral.centerCoordinates"
				:zoom="zoom"
				:min-zoom="minZoom"
				:zoom-control="false"
				:max-zoom="maxZoom"
				v-bind="MapCondominiumConfig"
				class="h-[340px] md:h-[340px]"
			>
				<slot name="markers">
					<GmapQuadrilateral
						v-if="props.showQuadrilateral"
						:center-coordinates="null"
						:paths-coordinates="quadrilateral.pathsCoordinates"
						:count="1"
						:group-key="quadrilateral.groupKey"
						:is-selected="true"
						:is-clickable="false"
					/>
					<GmapMarker
						v-if="props.showMarker"
						:position="centerCoordinates || quadrilateral.centerCoordinates"
						:gmp-draggable="false"
					/>
				</slot>
			</GmapMap>
		</ClientOnly>

		<template v-if="props.variant === 'primary'">
			<div :class="cn(sectionVariant({ type: 'primary' }), props.sectionClass)">
				<slot name="left">
					<Skeleton
						:is-active="isLoading"
						variant="primary-light-1"
						class="flex size-full w-full flex-col pb-5 md:w-1/2 md:gap-1 md:pb-0"
					>
						<slot name="address">
							<span class="text-base font-medium md:mt-1">
								{{ `${address}, ${number || ''}` }}
							</span>
							<span class="text-sm font-normal">
								{{ `${region}, ${city} - ${state}` }}
							</span>
						</slot>
					</Skeleton>
				</slot>

				<slot name="right">
					<Skeleton
						:is-active="isLoading"
						variant="primary-light-1"
						class="flex flex-1 flex-col justify-end md:flex-row"
					>
						<div class="flex flex-col gap-4 md:flex-row md:gap-8">
							<TextLink
								class="flex items-center gap-1 text-sm"
								variant="dark"
								@click="handleCtaClicked"
							>
								<IconMaterialSymbolsLightMapOutline class="size-5 text-white" />
								{{ props.cta }}
							</TextLink>
							<slot name="another-links" />
						</div>
					</Skeleton>
				</slot>
			</div>
		</template>
	</div>
</template>
